import { useEffect, useState } from 'react';
import { Link as RouterLink, Outlet } from 'react-router-dom';
import { Pista } from './Pista';
import { Seo } from './Seo';
import { NavLink } from './nav';
import { useSelection } from '../state/selection';
import { useAppData } from '../state/AppData';
import { CityPicker } from './CityPicker';
import { IconAula, IconBuscar, IconCompartir, IconExplorar, IconItinerarios, IconLeer, IconProyecto, IconRetos, IconVolumen } from './icons';

// El icono acompaña al rótulo, no lo sustituye: quien no reconozca el dibujo lee la palabra.
const NAV = [
  { to: '/leer', label: 'Leer', Icon: IconLeer },
  { to: '/explorar', label: 'Explorar', Icon: IconExplorar },
  { to: '/itinerarios', label: 'Itinerarios', Icon: IconItinerarios },
  { to: '/retos', label: 'Retos', Icon: IconRetos },
  { to: '/buscar', label: 'Buscar', Icon: IconBuscar },
  { to: '/aula', label: 'Aula', Icon: IconAula },
  { to: '/volumen', label: 'El volumen', Icon: IconVolumen },
  { to: '/proyecto/fuentes', label: 'Sobre el proyecto', Icon: IconProyecto },
];

export function Shell() {
  const { announcement, announce } = useSelection();
  const { city } = useAppData();
  // La aplicación fija el alto de la ventana y desplaza dentro de cada panel. La portada vive fuera
  // de este armazón y se desplaza como una página normal, así que el bloqueo se marca aquí y se
  // retira al salir; si no, la portada queda recortada a la altura de la pantalla.
  useEffect(() => {
    // va en <html>: el alto en porcentaje de <body> solo se resuelve si su padre tiene alto fijo
    document.documentElement.classList.add('is-app');
    return () => document.documentElement.classList.remove('is-app');
  }, []);
  return (
    <div className="shell">
      <Seo />
      <nav className="skip-links" aria-label="Saltar">
        <a href="#contenido">Saltar al contenido</a>
        <a href="#texto">Saltar al texto</a>
        <a href="#lista-lugares">Saltar a la lista de lugares</a>
      </nav>
      <header className="masthead">
        <RouterLink to="/" className="masthead__home" aria-label="Portada del proyecto">1884.es</RouterLink>
        <NavLink to="/ciudad" className="masthead__brand">
          <span className="masthead__title">{city.title}</span>
          <span className="masthead__subtitle">Leer el territorio, recorrer el libro</span>
        </NavLink>
        <CityPicker />
        <nav aria-label="Modos" className="masthead__nav">
          <ul>
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} className={({ isActive }) => (isActive ? 'is-active' : undefined)}>
                  <n.Icon className="masthead__icon" />
                  <span>{n.label}</span>
                </NavLink>
              </li>
            ))}
            <li><Compartir onAviso={announce} /></li>
          </ul>
        </nav>
      </header>
      <Pista />
      <main id="contenido" className="shell__main" tabIndex={-1}>
        <Outlet />
      </main>
      <div aria-live="polite" aria-atomic="true" className="visually-hidden" data-testid="announcer">{announcement}</div>
    </div>
  );
}

/** Comparte la dirección de lo que se está viendo: el capítulo, el lugar o el pasaje exacto.
 *
 *  En el móvil abre la hoja de compartir del propio sistema —WhatsApp, Telegram, correo, lo que el
 *  usuario tenga—, que es lo que la gente espera y lo único que no obliga a incrustar botones de
 *  terceros: aquí no hay cookies ni analítica, y un botón de WhatsApp embebido traería rastreo.
 *  Donde no existe esa hoja (casi todo el escritorio) copia el enlace al portapapeles y lo avisa.
 */
function Compartir({ onAviso }: { onAviso: (mensaje: string) => void }) {
  const [copiado, setCopiado] = useState(false);
  const compartir = async () => {
    const url = window.location.href;
    const title = document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, text: `${title} · 1884.es`, url });
      } catch { /* el usuario cerró la hoja de compartir: no es un error */ }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      onAviso('Enlace copiado al portapapeles.');
      window.setTimeout(() => setCopiado(false), 2000);
    } catch {
      onAviso('No se pudo copiar el enlace. Está en la barra de direcciones.');
    }
  };
  return (
    <button type="button" className="masthead__share" onClick={compartir}
      aria-label="Compartir esta página">
      <IconCompartir className="masthead__icon" />
      <span>{copiado ? '¡Copiado!' : 'Compartir'}</span>
    </button>
  );
}
